import type { RefObject } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { Dropdown, PlainTooltip } from "@similarweb/ui-components";

import {
    CREATE_TOPICS_ITEM_ID,
    DROPDOWN_CONFIG,
    CREATE_TOPIC_FROM_KW_ITEM_ID,
    TOPIC_HEADER_ID,
} from "app/pages/activation/components/Topics/Dropdown/config";
import { activationTracking } from "app/pages/activation/services/tracking";
import { translate } from "app/filters/i18nFilter";
import { MAXIMUM_TOPICS } from "app/pages/activation/components/Topics/constants";
import {
    ADD_BTN_TEXT,
    CREATE_TOPICS_SUBTITLE,
    CREATE_TOPICS_TITLE,
    IMPORT_BTN_SUBTITLE,
    IMPORT_BTN_TITLE,
    MAXIMUM_TOPICS_CREATED_ADD_BTN_TEXT,
} from "app/pages/activation/components/Topics/texts";

import { dropdownKeys } from "../automationKeys";

import * as SC from "./styles";

type TopicsDropdownSchema = {
    isMaxCreated: boolean;
    dropdownRef: RefObject<any>;
    onClick: (item: { id: string }) => void;
};

export const TopicsDropdown = ({ isMaxCreated, dropdownRef, onClick }: TopicsDropdownSchema) => {
    const track = activationTracking.home;

    const onToggle = (isOpen: boolean) => {
        if (isOpen) {
            track.trackTopicOpenDropdown();
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            onToggle={onToggle}
            onClick={onClick}
            width={DROPDOWN_CONFIG.width}
            buttonWidth={DROPDOWN_CONFIG.buttonWidth}
            appendTo={`#${TOPIC_HEADER_ID}`}
            disabled={isMaxCreated}
        >
            {[
                <PlainTooltip
                    tooltipContent={translate(MAXIMUM_TOPICS_CREATED_ADD_BTN_TEXT, {
                        max: MAXIMUM_TOPICS,
                    })}
                    enabled={isMaxCreated}
                    placement="top"
                    key="add-topic"
                >
                    <span data-automation={dropdownKeys.maxTooltip}>
                        <SC.StyledIconButton
                            iconSize="xs"
                            placement="left"
                            type="outlined"
                            iconName="plus"
                            isDisabled={isMaxCreated}
                        >
                            {translate(ADD_BTN_TEXT)}
                        </SC.StyledIconButton>
                    </span>
                </PlainTooltip>,

                <SC.DropdownItemWrapper
                    key={CREATE_TOPICS_ITEM_ID}
                    id={CREATE_TOPICS_ITEM_ID}
                    data-automation={dropdownKeys.createTopic}
                >
                    <SWReactIcons
                        iconName="add"
                        size="sm"
                    />

                    <SC.ItemTextWrapper>
                        <SC.ItemTitle>{translate(CREATE_TOPICS_TITLE)}</SC.ItemTitle>
                        <SC.ItemDescription>{translate(CREATE_TOPICS_SUBTITLE)}</SC.ItemDescription>
                    </SC.ItemTextWrapper>
                </SC.DropdownItemWrapper>,

                <SC.DropdownItemWrapper
                    key={CREATE_TOPIC_FROM_KW_ITEM_ID}
                    id={CREATE_TOPIC_FROM_KW_ITEM_ID}
                    data-automation={dropdownKeys.createTopicFromKW}
                >
                    <SWReactIcons
                        iconName="nav-keyword-group"
                        size="sm"
                    />

                    <SC.ItemTextWrapper data-automation={dropdownKeys.text}>
                        <SC.ItemTitle>{translate(IMPORT_BTN_TITLE)}</SC.ItemTitle>
                        <SC.ItemDescription>{translate(IMPORT_BTN_SUBTITLE)}</SC.ItemDescription>
                    </SC.ItemTextWrapper>
                </SC.DropdownItemWrapper>,
            ]}
        </Dropdown>
    );
};

TopicsDropdown.displayName = "TopicsDropdown";
